// @flow

import React from 'react';
import { array, number, object } from 'yup';

import { isLastElementInArray } from '../common-utils/utils';
import {
  TierEndCell,
  TierLabelCell,
  TierStartCell,
  UsagePercentageCell,
} from '../components/advanced-security/AdvancedSecurityTierCells';
import { DefaultTierValuesTable } from '../components/advanced-security/DefaultTierValuesTable';
import {
  ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  ADVANCED_SECURITY_TIERS_BACKEND_NAME,
  ADVANCED_SECURITY_TIER_END_BACKEND_NAME,
  ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME,
} from '../constants';

// Validates each editable Tier End: required, greater than 0, and strictly greater than the
// previous tier's end. The last tier always extends to Infinity, so its end is never entered
// and is not validated.
const advancedSecurityTierEndValidation = number()
  .nullable()
  .label('Tier End')
  .typeError('Tier End must be a number!')
  .test('validAdvancedSecurityTierEnd', 'Invalid Tier End', (value, { createError, from }) => {
    const currentTier = from[0].value;
    const parentInputs = from[1].value;
    const tiers = parentInputs[ADVANCED_SECURITY_TIERS_BACKEND_NAME] ?? [];
    const tierIndex = tiers.indexOf(currentTier);

    // The last tier always extends to Infinity — its end is never entered, so nothing to validate.
    if (isLastElementInArray(tiers, tierIndex)) {
      return true;
    }

    if (value == null || Number.isNaN(value)) {
      return createError({ message: 'Tier End is required!' });
    }

    // Tier 1 starts at 0, so its end only needs to be positive.
    if (tierIndex === 0) {
      return value > 0 ? true : createError({ message: 'Tier End must be greater than 0!' });
    }

    // Only Tier 2 reaches here — its end must be greater than Tier 1's end.
    const tier1End = Number(tiers[0][ADVANCED_SECURITY_TIER_END_BACKEND_NAME]);
    if (value <= tier1End) {
      return createError({
        message: `Tier End must be greater than the previous tier's end (${tier1End}).`,
      });
    }
    return true;
  });

// Per-tier usage percentage: required, between 0 and 100.
const advancedSecurityUsagePercentageValidation = number()
  .label('% of Usage')
  .typeError('% of Usage is required!')
  .min(0, '% of Usage must be at least 0!')
  .max(100, '% of Usage cannot exceed 100!')
  .required('% of Usage is required!');

export const ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_CONFIG = {
  backendFieldName: ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  // {} until tiers are configured; once present, every tier's end + usage percentage is validated.
  validation: object({
    [ADVANCED_SECURITY_TIERS_BACKEND_NAME]: array(
      object({
        [ADVANCED_SECURITY_TIER_END_BACKEND_NAME]: advancedSecurityTierEndValidation,
        [ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME]:
          advancedSecurityUsagePercentageValidation,
      })
    ),
  }).default({}),
};

// Declarative description of the tier table's columns: header text + width + order, and which
// component renders each cell. The cells read their per-row data from FixedRowsGridRowContext.
// Widths are Semantic UI Grid units and sum to 16 (2 + 5 + 5 + 4).
export const ADVANCED_SECURITY_TIERS_COLUMNS_CONFIGURATION = [
  {
    key: 'label',
    headerText: '',
    width: 2,
    CellComponent: TierLabelCell,
  },
  {
    key: 'tierStart',
    headerText: 'Tier Start ($)',
    width: 5,
    CellComponent: TierStartCell,
  },
  {
    key: 'tierEnd',
    headerText: 'Tier End ($)',
    width: 5,
    CellComponent: TierEndCell,
    headerTooltip: <DefaultTierValuesTable />,
  },
  {
    key: 'usagePercentage',
    headerText: '% of Usage',
    width: 4,
    CellComponent: UsagePercentageCell,
  },
];
